import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  inject,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { ContextService } from '@core/providers/context/context.service';
import { KeyboardStatusService } from '@core/providers/keyboard-status/keyboard-status.service';
import { KeyboardUtils } from '@core/utils/events/keyboard.utils';
import {
  DialogActionsDirective,
  DialogComponent,
  DialogContentDirective,
  DialogTitleDirective,
} from '@shared/components/dialog/dialog.component';
import { GroupsService } from '@shared/components/groups/groups.service';
import { ValidationErrorsComponent } from '@shared/components/validation-errors/validation-errors.component';
import { CONSTANTS } from '@shared/constants';
import { ErrorsDirective } from '@shared/directives/error/errors.directive';
import { FormModels } from '@shared/form-models';
import { IGroupPayload } from '@shared/interfaces/groups/add-group-payload.interface';
import { IGroup } from '@shared/interfaces/groups/group.interface';

export const newCategoryDialogConfig: MatDialogConfig = {
  width: '480px',
  disableClose: true,
  panelClass: 'dialog-fit-content',
};

@Component({
  selector: 'app-new-category-dialog',
  templateUrl: './new-category-dialog.component.html',
  styleUrls: ['./new-category-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(window:keyup)': 'keyEvent($event)' },
  imports: [
    ReactiveFormsModule,
    DialogComponent,
    DialogTitleDirective,
    DialogContentDirective,
    DialogActionsDirective,
    MatButton,
    MatDialogClose,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    ErrorsDirective,
    ValidationErrorsComponent,
    TranslatePipe,
  ],
})
export class NewCategoryDialogComponent implements OnInit {
  data = inject<IGroup | null>(MAT_DIALOG_DATA);

  errors = FormModels.AccountAccount.CategoryGroupErrors;
  accountChildrenNamePlural = '';

  readonly nameInputRef = viewChild<ElementRef>('nameInput');

  private mdDialogRef = inject<MatDialogRef<NewCategoryDialogComponent, IGroup>>(MatDialogRef);
  private groupsService = inject(GroupsService);
  private formBuilder = inject(FormBuilder);
  private keyboardStatusService = inject(KeyboardStatusService);
  private contextService = inject(ContextService);
  private destroyRef = inject(DestroyRef);

  private categories: IGroup[] = [];
  private groups: IGroup[] = [];
  private escModalStatus = false;

  readonly categoryForm: FormGroup = this.createForm();

  get selectedCategory(): IGroup | null {
    return this.data ?? null;
  }

  keyEvent(event: KeyboardEvent) {
    if (KeyboardUtils.pressedEscape(event) && this.escModalStatus) {
      this.mdDialogRef.close();
    }
  }

  ngOnInit() {
    this.accountChildrenNamePlural = this.contextService.getAccountChildrenNameTranslation(false);
    combineLatest([this.groupsService.getCategories$(), this.groupsService.getGroups$()])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([categories, groups]: [IGroup[], IGroup[]]) => {
        this.categories = categories;
        this.groups = groups;
        this.categoryForm.controls.name.updateValueAndValidity();
      });
    this.keyboardStatusService
      .getEscModalStatus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(status => (this.escModalStatus = status));
    this.focusNameInput();
  }

  save() {
    const selectedCategory = this.selectedCategory;

    if (this.categoryForm.valid) {
      if (selectedCategory) {
        this.updateExisting(selectedCategory);
      } else {
        this.saveNew();
      }
    } else {
      this.categoryForm.controls.name.markAsTouched();
      this.categoryForm.controls.name.markAsDirty();
    }
  }

  private createForm(): FormGroup {
    const minLength = 2;

    return this.formBuilder.group({
      name: new FormControl(this.selectedCategory?.name ?? '', [
        Validators.required,
        Validators.minLength(minLength),
        this.isUniqueCategoryName.bind(this),
      ]),
    });
  }

  private isUniqueCategoryName(control: AbstractControl) {
    const allGroups = [...this.categories, ...this.groups];
    const selectedCategory = this.selectedCategory;
    const otherGroups = selectedCategory
      ? allGroups.filter(group => group.uuid !== selectedCategory.uuid)
      : allGroups;
    const isExisting = !!otherGroups.find(group => group.name === control.value);

    return isExisting ? { isUnique: {} } : null;
  }

  private updateExisting(selectedCategory: IGroup) {
    const payload: IGroupPayload = {
      name: this.categoryForm.controls.name.value,
      isCategory: selectedCategory.isCategory,
      category: null,
      venues: [],
      venueGroups: (selectedCategory.venueGroups ?? []).map(group => group.uuid),
    };

    this.groupsService
      .updateGroup$(selectedCategory.uuid, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(updatedCategory => this.handleResponseData(updatedCategory));
  }

  private saveNew() {
    const payload: IGroupPayload = {
      name: this.categoryForm.controls.name.value,
      isCategory: true,
      category: null,
      venues: [],
      venueGroups: [],
    };

    this.groupsService
      .addGroup$(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(newCategory => this.handleResponseData(newCategory));
  }

  private handleResponseData(category: IGroup | null) {
    if (category?.uuid) {
      this.mdDialogRef.close(category);
    }
  }

  private focusNameInput() {
    setTimeout(() => {
      this.nameInputRef()?.nativeElement.focus();
    }, CONSTANTS.TIME_IN_MS.TIME_250);
  }
}
